import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, Users, GraduationCap, BookOpen, ClipboardList, 
  Calendar, FileText, BarChart3, Settings, LogOut, ChevronLeft,
  UserCheck, BookMarked, Award, User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useState } from 'react';
import clsx from 'clsx';

const adminMenuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/admin/students', icon: Users, label: 'Manage Students' },
  { path: '/admin/lecturers', icon: GraduationCap, label: 'Manage Lecturers' },
  { path: '/admin/subjects', icon: BookOpen, label: 'Subjects' },
  { path: '/admin/exams', icon: ClipboardList, label: 'Exams' },
  { path: '/admin/attendance', icon: Calendar, label: 'Attendance' },
  { path: '/admin/grades', icon: Award, label: 'Grades' },
  { path: '/admin/reports', icon: BarChart3, label: 'Reports & Analytics' },
];

const lecturerMenuItems = [
  { path: '/lecturer', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/lecturer/subjects', icon: BookOpen, label: 'My Subjects' },
  { path: '/lecturer/students', icon: Users, label: 'My Students' },
  { path: '/lecturer/exams', icon: ClipboardList, label: 'Exams & Marks' },
  { path: '/lecturer/attendance', icon: UserCheck, label: 'Attendance' },
  { path: '/lecturer/reports', icon: BarChart3, label: 'Reports' },
  { path: '/lecturer/profile', icon: User, label: 'My Profile' },
];

const studentMenuItems = [
  { path: '/student', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { path: '/student/subjects', icon: BookMarked, label: 'My Subjects' },
  { path: '/student/results', icon: Award, label: 'Results & Grades' },
  { path: '/student/attendance', icon: Calendar, label: 'Attendance' },
  { path: '/student/exams', icon: FileText, label: 'Upcoming Exams' },
  { path: '/student/profile', icon: User, label: 'My Profile' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const getMenuItems = () => {
    switch (user?.roleId) {
      case 1: return adminMenuItems;
      case 2: return lecturerMenuItems;
      case 3: return studentMenuItems;
      default: return [];
    }
  };

  const getRoleName = () => {
    switch (user?.roleId) {
      case 1: return 'Administrator';
      case 2: return 'Lecturer';
      case 3: return 'Student';
      default: return 'User';
    }
  };

  return (
    <aside className={clsx(
      'hidden lg:flex bg-gradient-to-b from-indigo-900 to-purple-900 text-white flex-col transition-all duration-300',
      collapsed ? 'w-20' : 'w-64'
    )}>
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div>
              <h1 className="text-xl font-bold">LIT Academic</h1>
              <p className="text-xs text-indigo-300">Literature Management</p>
            </div>
          )}
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className={clsx('w-5 h-5 transition-transform', collapsed && 'rotate-180')} />
          </button>
        </div>
      </div>

      {/* User Info */}
      <div className={clsx('p-4 border-b border-white/10', collapsed && 'px-2')}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-orange-400 flex items-center justify-center font-bold">
            {user?.fullName?.charAt(0) || 'U'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.fullName}</p>
              <p className="text-xs text-indigo-300">{getRoleName()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {getMenuItems().map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) => clsx(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
              isActive 
                ? 'bg-white/20 text-white shadow-lg' 
                : 'text-indigo-200 hover:bg-white/10 hover:text-white',
              collapsed && 'justify-center'
            )}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={logout}
          className={clsx(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg w-full text-indigo-200 hover:bg-red-500/20 hover:text-red-300 transition-all',
            collapsed && 'justify-center'
          )}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
